import type {
  ApiErrorBody,
  BodyMetric,
  DayExercise,
  DayExercisePatch,
  Exercise,
  FinishWorkoutOptions,
  FinishWorkoutResult,
  LoginRequest,
  MuscleGroupVolume,
  NewBodyMetric,
  NewDayExercise,
  NewExercise,
  NewProgram,
  NewWorkout,
  PeriodKind,
  PeriodSummary,
  PersonalRecord,
  PrCountPoint,
  ProgressionPoint,
  Program,
  SessionStatus,
  TrainingDay,
  Workout,
  WorkoutComparison,
  WorkoutExercise,
  WorkoutListItem,
  WorkoutPatch,
  WorkoutSet,
  WorkoutSetPatch,
} from "../../../shared/src";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface JsonRequestInit extends Omit<RequestInit, "body"> {
  json?: unknown;
}

async function jsonRequest<T>(
  url: string,
  init: JsonRequestInit = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: {
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body: json !== undefined ? JSON.stringify(json) : undefined,
    ...rest,
  });

  const text = await res.text();
  let body: unknown = null;
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    const errBody = body as Partial<ApiErrorBody> | null;
    const code = errBody?.error?.code ?? "ERROR";
    const message = errBody?.error?.message ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, code, message);
  }

  return body as T;
}

export async function login(password: string): Promise<void> {
  const payload: LoginRequest = { password };
  await jsonRequest<{ ok: true }>("/api/auth/login", {
    method: "POST",
    json: payload,
  });
}

export async function logout(): Promise<void> {
  await jsonRequest<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

export async function fetchSession(): Promise<SessionStatus> {
  return jsonRequest<SessionStatus>("/api/auth/session");
}

export async function listExercises(
  opts: { includeArchived?: boolean } = {},
): Promise<Exercise[]> {
  const qs = opts.includeArchived ? "?includeArchived=true" : "";
  return jsonRequest<Exercise[]>(`/api/exercises${qs}`);
}

export async function createExercise(input: NewExercise): Promise<Exercise> {
  return jsonRequest<Exercise>("/api/exercises", {
    method: "POST",
    json: input,
  });
}

export async function updateExercise(
  id: number,
  patch: Partial<NewExercise>,
): Promise<Exercise> {
  return jsonRequest<Exercise>(`/api/exercises/${id}`, {
    method: "PATCH",
    json: patch,
  });
}

export async function archiveExercise(id: number): Promise<void> {
  await jsonRequest<void>(`/api/exercises/${id}/archive`, { method: "POST" });
}

export async function unarchiveExercise(id: number): Promise<void> {
  await jsonRequest<void>(`/api/exercises/${id}/unarchive`, { method: "POST" });
}

export async function listPrograms(
  opts: { includeArchived?: boolean; active?: boolean } = {},
): Promise<Program[]> {
  const params: string[] = [];
  if (opts.includeArchived) params.push("includeArchived=true");
  if (opts.active) params.push("active=true");
  const qs = params.length ? `?${params.join("&")}` : "";
  return jsonRequest<Program[]>(`/api/programs${qs}`);
}

export async function getProgram(id: number): Promise<Program> {
  return jsonRequest<Program>(`/api/programs/${id}`);
}

export async function createProgram(input: NewProgram): Promise<Program> {
  return jsonRequest<Program>("/api/programs", {
    method: "POST",
    json: input,
  });
}

export async function updateProgram(
  id: number,
  patch: Partial<NewProgram>,
): Promise<Program> {
  return jsonRequest<Program>(`/api/programs/${id}`, {
    method: "PATCH",
    json: patch,
  });
}

export async function archiveProgram(id: number): Promise<void> {
  await jsonRequest<void>(`/api/programs/${id}/archive`, { method: "POST" });
}

export async function unarchiveProgram(id: number): Promise<void> {
  await jsonRequest<void>(`/api/programs/${id}/unarchive`, { method: "POST" });
}

export async function activateProgram(id: number): Promise<void> {
  await jsonRequest<void>(`/api/programs/${id}/activate`, { method: "POST" });
}

export async function deactivateProgram(id: number): Promise<void> {
  await jsonRequest<void>(`/api/programs/${id}/deactivate`, { method: "POST" });
}

export async function listTrainingDays(
  programId: number,
): Promise<TrainingDay[]> {
  return jsonRequest<TrainingDay[]>(`/api/programs/${programId}/days`);
}

export async function getTrainingDay(id: number): Promise<TrainingDay> {
  return jsonRequest<TrainingDay>(`/api/training-days/${id}`);
}

export async function createTrainingDay(
  programId: number,
  name: string,
): Promise<TrainingDay> {
  return jsonRequest<TrainingDay>(`/api/programs/${programId}/days`, {
    method: "POST",
    json: { name },
  });
}

export async function renameTrainingDay(
  id: number,
  name: string,
): Promise<TrainingDay> {
  return jsonRequest<TrainingDay>(`/api/training-days/${id}`, {
    method: "PATCH",
    json: { name },
  });
}

export async function deleteTrainingDay(id: number): Promise<void> {
  await jsonRequest<void>(`/api/training-days/${id}`, { method: "DELETE" });
}

export async function reorderTrainingDays(
  programId: number,
  ids: number[],
): Promise<TrainingDay[]> {
  return jsonRequest<TrainingDay[]>(
    `/api/programs/${programId}/days/reorder`,
    { method: "POST", json: { ids } },
  );
}

export async function listDayExercises(dayId: number): Promise<DayExercise[]> {
  return jsonRequest<DayExercise[]>(`/api/training-days/${dayId}/exercises`);
}

export async function addDayExercise(
  dayId: number,
  input: Omit<NewDayExercise, "training_day_id">,
): Promise<DayExercise> {
  return jsonRequest<DayExercise>(`/api/training-days/${dayId}/exercises`, {
    method: "POST",
    json: input,
  });
}

export async function updateDayExercise(
  id: number,
  patch: DayExercisePatch,
): Promise<DayExercise> {
  return jsonRequest<DayExercise>(`/api/day-exercises/${id}`, {
    method: "PATCH",
    json: patch,
  });
}

export async function removeDayExercise(id: number): Promise<void> {
  await jsonRequest<void>(`/api/day-exercises/${id}`, { method: "DELETE" });
}

export async function reorderDayExercises(
  dayId: number,
  ids: number[],
): Promise<DayExercise[]> {
  return jsonRequest<DayExercise[]>(
    `/api/training-days/${dayId}/exercises/reorder`,
    { method: "POST", json: { ids } },
  );
}

export async function startWorkout(input: NewWorkout = {}): Promise<Workout> {
  return jsonRequest<Workout>("/api/workouts", {
    method: "POST",
    json: input,
  });
}

export async function getWorkout(id: number): Promise<Workout> {
  return jsonRequest<Workout>(`/api/workouts/${id}`);
}

export async function getActiveWorkout(): Promise<Workout | null> {
  return jsonRequest<Workout | null>("/api/workouts/active");
}

async function rawFinishWorkout(
  id: number,
  opts?: FinishWorkoutOptions,
): Promise<FinishWorkoutResult> {
  const hasOpts =
    opts !== undefined &&
    (opts.started_at !== undefined || opts.ended_at !== undefined);
  return jsonRequest<FinishWorkoutResult>(`/api/workouts/${id}/finish`, {
    method: "POST",
    ...(hasOpts ? { json: opts } : {}),
  });
}

export async function finishWorkout(
  id: number,
  opts?: FinishWorkoutOptions,
): Promise<FinishWorkoutResult> {
  const { withQueueFinishWorkout } = await import("../offline/withQueue");
  return withQueueFinishWorkout(() => rawFinishWorkout(id, opts), id, opts);
}

export const _rawForReplay = {
  addWorkoutSet: rawAddWorkoutSet,
  updateWorkoutSet: rawUpdateWorkoutSet,
  deleteWorkoutSet: rawDeleteWorkoutSet,
  finishWorkout: rawFinishWorkout,
};

export async function updateWorkout(
  id: number,
  patch: WorkoutPatch,
): Promise<Workout> {
  return jsonRequest<Workout>(`/api/workouts/${id}`, {
    method: "PATCH",
    json: patch,
  });
}

export async function listRecentWorkouts(
  limit = 20,
): Promise<WorkoutListItem[]> {
  return jsonRequest<WorkoutListItem[]>(`/api/workouts?limit=${limit}`);
}

export async function deleteWorkout(id: number): Promise<void> {
  await jsonRequest<void>(`/api/workouts/${id}`, { method: "DELETE" });
}

export async function listWorkoutSets(
  workoutId: number,
): Promise<WorkoutSet[]> {
  return jsonRequest<WorkoutSet[]>(`/api/workouts/${workoutId}/sets`);
}

async function rawAddWorkoutSet(
  workoutId: number,
  input: { exercise_id: number; weight_kg: number; reps: number },
): Promise<WorkoutSet> {
  return jsonRequest<WorkoutSet>(`/api/workouts/${workoutId}/sets`, {
    method: "POST",
    json: input,
  });
}

export async function addWorkoutSet(
  workoutId: number,
  input: { exercise_id: number; weight_kg: number; reps: number },
): Promise<WorkoutSet> {
  const { withQueueAddWorkoutSet } = await import("../offline/withQueue");
  return withQueueAddWorkoutSet(
    () => rawAddWorkoutSet(workoutId, input),
    { workout_id: workoutId, ...input },
  );
}

async function rawUpdateWorkoutSet(
  id: number,
  patch: WorkoutSetPatch,
): Promise<WorkoutSet> {
  return jsonRequest<WorkoutSet>(`/api/workout-sets/${id}`, {
    method: "PATCH",
    json: patch,
  });
}

export async function updateWorkoutSet(
  id: number,
  patch: WorkoutSetPatch,
): Promise<WorkoutSet> {
  const { withQueueUpdateWorkoutSet } = await import("../offline/withQueue");
  return withQueueUpdateWorkoutSet(
    () => rawUpdateWorkoutSet(id, patch),
    { set_id: id, patch },
  );
}

async function rawDeleteWorkoutSet(id: number): Promise<void> {
  await jsonRequest<void>(`/api/workout-sets/${id}`, { method: "DELETE" });
}

export async function deleteWorkoutSet(id: number): Promise<void> {
  const { withQueueDeleteWorkoutSet } = await import("../offline/withQueue");
  return withQueueDeleteWorkoutSet(() => rawDeleteWorkoutSet(id), id);
}


export async function reorderWorkoutSets(
  workoutId: number,
  exerciseId: number,
  ids: number[],
): Promise<WorkoutSet[]> {
  return jsonRequest<WorkoutSet[]>(
    `/api/workouts/${workoutId}/exercises/${exerciseId}/sets/reorder`,
    { method: "POST", json: { ids } },
  );
}

export async function getPreviousSetsForExercise(
  workoutId: number,
  exerciseId: number,
): Promise<WorkoutSet[]> {
  return jsonRequest<WorkoutSet[]>(
    `/api/workouts/${workoutId}/previous-sets?exercise_id=${exerciseId}`,
  );
}

export async function listWorkoutExercises(
  workoutId: number,
): Promise<WorkoutExercise[]> {
  return jsonRequest<WorkoutExercise[]>(`/api/workouts/${workoutId}/exercises`);
}

export async function removeExerciseFromWorkout(
  workoutId: number,
  exerciseId: number,
): Promise<void> {
  await jsonRequest<void>(
    `/api/workouts/${workoutId}/exercises/${exerciseId}`,
    { method: "DELETE" },
  );
}

export async function reorderAdhocExercises(
  workoutId: number,
  exerciseIds: number[],
): Promise<void> {
  await jsonRequest<void>(`/api/workouts/${workoutId}/adhoc-order`, {
    method: "PATCH",
    json: { exercise_ids: exerciseIds },
  });
}

export async function getExerciseProgression(
  exerciseId: number,
): Promise<ProgressionPoint[]> {
  return jsonRequest<ProgressionPoint[]>(
    `/api/analytics/exercises/${exerciseId}/progression`,
  );
}

export async function getExercisePersonalRecords(
  exerciseId: number,
): Promise<PersonalRecord[]> {
  return jsonRequest<PersonalRecord[]>(
    `/api/analytics/exercises/${exerciseId}/personal-records`,
  );
}

export async function getPeriodSummaries(opts: {
  period: PeriodKind;
  since?: string;
  limit?: number;
}): Promise<PeriodSummary[]> {
  const params = new URLSearchParams({ period: opts.period });
  if (opts.since !== undefined) params.set("since", opts.since);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  return jsonRequest<PeriodSummary[]>(
    `/api/analytics/summaries?${params.toString()}`,
  );
}

export async function getVolumeByMuscleGroup(opts: {
  period: PeriodKind;
  since?: string;
  limit?: number;
}): Promise<MuscleGroupVolume[]> {
  const params = new URLSearchParams({ period: opts.period });
  if (opts.since !== undefined) params.set("since", opts.since);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  return jsonRequest<MuscleGroupVolume[]>(
    `/api/analytics/volume-by-muscle-group?${params.toString()}`,
  );
}

export async function getPrsByPeriod(opts: {
  period: PeriodKind;
  since?: string;
  limit?: number;
}): Promise<PrCountPoint[]> {
  const params = new URLSearchParams({ period: opts.period });
  if (opts.since !== undefined) params.set("since", opts.since);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  return jsonRequest<PrCountPoint[]>(
    `/api/analytics/prs-by-period?${params.toString()}`,
  );
}

export async function getWorkoutComparison(
  workoutId: number,
): Promise<WorkoutComparison> {
  return jsonRequest<WorkoutComparison>(
    `/api/workouts/${workoutId}/compare-previous`,
  );
}

export async function listBodyMetrics(
  opts: { limit?: number; since?: string } = {},
): Promise<BodyMetric[]> {
  const params = new URLSearchParams();
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.since !== undefined) params.set("since", opts.since);
  const qs = params.toString();
  return jsonRequest<BodyMetric[]>(
    `/api/body-metrics${qs ? `?${qs}` : ""}`,
  );
}

export async function addBodyMetric(
  input: NewBodyMetric,
): Promise<BodyMetric> {
  return jsonRequest<BodyMetric>("/api/body-metrics", {
    method: "POST",
    json: input,
  });
}

export async function deleteBodyMetric(id: number): Promise<void> {
  await jsonRequest<void>(`/api/body-metrics/${id}`, { method: "DELETE" });
}

export async function getExercise(id: number): Promise<Exercise> {
  return jsonRequest<Exercise>(`/api/exercises/${id}`);
}
